/**
 * CS-2 — the shared Spec Sheet, read by anybody with the link.
 *
 * No login, no session: the token in the path is the whole credential, exactly
 * as the Material Tag's /m/{code} page works. The payload is a whitelist built
 * on the server, so this page cannot show a field the firm did not share.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '@/styles/spec-sheet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (value) => {
  if (!value) return '';
  const when = new Date(value);
  return Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString();
};

export default function PublicSpecSheet() {
  const { token } = useParams();
  const [sheet, setSheet] = useState(null);
  const [gone, setGone] = useState(false);
  const [shown, setShown] = useState(0);

  // PR-2 — the printed sheet is a PDF from the share's own door; this page no
  // longer asks the browser to print anything.
  const [pdfBusy, setPdfBusy] = useState(false);
  const makePdf = async () => {
    try {
      setPdfBusy(true);
      const answer = await axios.get(`${API}/public/spec-sheet/${token}/print.pdf`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([answer.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    axios.get(`${API}/public/spec-sheet/${token}`)
      .then(({ data }) => setSheet(data))
      .catch(() => setGone(true));
  }, [token]);

  if (gone) {
    return (
      <div className="ksp ksp--public" data-testid="public-sheet-gone">
        <div className="ksp__foot">This sheet is no longer being shared.</div>
      </div>
    );
  }
  if (!sheet) {
    return <div className="ksp ksp--public" data-testid="public-sheet-loading"><div className="ksp__foot">Loading…</div></div>;
  }

  const images = sheet.images || [];
  const hero = images[shown] || sheet.primary_image;
  const dimensions = sheet.dimensions
    || [sheet.width, sheet.depth, sheet.height].filter(Boolean).join(' · ') || '—';

  return (
    <div className="ksp ksp--public" data-testid="public-sheet">
      <div className="ksp__pub">
        {sheet.firm?.logo ? <img src={sheet.firm.logo} alt={sheet.firm?.name || 'Firm'} /> : null}
        <span className="nm" data-testid="public-sheet-firm">{sheet.firm?.name || ''}</span>
      </div>

      <div className="ksp__body">
        <div className="ksp__plate">
          <div className="ksp__hero">
            {hero ? <img src={`${process.env.REACT_APP_BACKEND_URL}${hero}`} alt={sheet.title} /> : null}
          </div>
          {images.length > 1 && (
            <div className="ksp__thumbs">
              {images.map((url, index) => (
                <button type="button" className={`img${index === shown ? ' on' : ''}`} key={url}
                  onClick={() => setShown(index)} data-testid={`public-sheet-thumb-${index}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`${sheet.title} ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
          {(sheet.attachments || []).length > 0 && (
            <div className="ksp__files" data-testid="public-sheet-files">
              <div className="hd">Spec sheets · {sheet.attachments.length}</div>
              {sheet.attachments.map((file) => (
                <div className="f" key={file.url}>
                  <span>{file.name}</span>
                  <span className="k" />
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`} target="_blank" rel="noreferrer">Open</a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ksp__main">
          <div className="ksp__id">
            {sheet.spec_num ? <span className="kso-chip" data-testid="public-sheet-specnum">{sheet.spec_num}</span> : null}
            {sheet.spec_category ? <span className="kso-chip">{sheet.spec_category}</span> : null}
          </div>
          <h1 className="ksp__title" data-testid="public-sheet-title">{sheet.title || 'Specification'}</h1>
          <div className="ksp__sub">
            {sheet.manufacturer ? <span><b>{sheet.manufacturer}</b></span> : null}
            {sheet.model ? <span>Model {sheet.model}</span> : null}
            {sheet.finish ? <span>{sheet.finish}</span> : null}
          </div>

          <div className="ksp__facts">
            {sheet.price ? (
              <div><span className="k">Price</span><span className="v big" data-testid="public-sheet-price">{sheet.price}</span></div>
            ) : null}
            <div><span className="k">Dimensions</span><span className="v">{dimensions}</span></div>
            <div><span className="k">Lead time</span><span className="v">{sheet.lead_time || '—'}</span></div>
            <div><span className="k">Per</span><span className="v">{sheet.unit || '—'}</span></div>
          </div>

          <div className="ksp__where">
            <div className="ksp__card">
              <div className="hd">Where it goes</div>
              <div className="bd">
                <div className="row"><span className="k">Room</span><span data-testid="public-sheet-room">{sheet.room || '—'}</span></div>
                <div className="row"><span className="k">Location</span><span>{sheet.location || '—'}</span></div>
                {sheet.plan ? (
                  <div className="row">
                    <span className="k">On the plan</span>
                    <span data-testid="public-sheet-plan">
                      {sheet.plan.points} point{sheet.plan.points === 1 ? '' : 's'}
                      {sheet.plan.page_number ? ` · page ${sheet.plan.page_number}` : ''}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
            {sheet.notes ? (
              <div className="ksp__card">
                <div className="hd">For the trade</div>
                <div className="bd"><span data-testid="public-sheet-notes">{sheet.notes}</span></div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="ksp__foot" data-testid="public-sheet-foot">
        Shared {fmtDate(sheet.shared_at)} · this sheet is live
        <button type="button" className="ksp__pdf" onClick={makePdf} disabled={pdfBusy}
          data-testid="public-sheet-pdf">
          {pdfBusy ? 'Making…' : 'Print / Save PDF'}
        </button>
      </div>
    </div>
  );
}
